import { formatCurrency } from "@/lib/currency";
import { supabase } from "@/integrations/supabase/client";

interface SettlementResult {
  success: boolean;
  invoiceCreated: boolean;
  invoiceId?: string;
  error?: string;
  skippedReason?: 'internal_job' | 'no_rate_card' | 'no_relationship' | 'already_exists';
  existingInvoiceId?: string;
  existingInvoiceNumber?: string;
}

/**
 * Triggers subcontract settlement after POD completion.
 * Called alongside triggerAutoInvoicing when a job is completed.
 * 
 * Flow:
 * 1. Check if job is subcontracted
 * 2. Find active rate card
 * 3. Create subcontract invoice record
 * 4. Generate branded PDF (same template as customer invoices)
 * 5. Auto-send email with PDF attached (if billing automation enabled)
 */
export async function triggerSubcontractSettlement(
  jobId: string,
  showToast?: (options: { title: string; description: string; variant?: "default" | "destructive" }) => void
): Promise<SettlementResult> {
  console.log('[SubcontractSettlement] Checking job:', jobId);

  try {
    // Fetch job with execution info and estimated cost for percent discount pricing
    const { data: job, error: jobError } = await supabase
      .from('jobs')
      .select(`
        id, company_id, executing_company_id, execution_type,
        vehicle_make, vehicle_model, vehicle_registration,
        collection_address, delivery_address, delivery_contact,
        delivery_completed_at, scheduled_date, invoice_number,
        estimated_cost, invoice_amount, has_return_vehicle,
        is_return_leg, parent_job_id
      `)
      .eq('id', jobId)
      .single();

    if (jobError || !job) {
      console.error('[SubcontractSettlement] Job fetch error:', jobError);
      return { success: false, invoiceCreated: false, error: jobError?.message || 'Job not found' };
    }

    // RETURN LEG REDIRECT: If this IS the return leg completing, trigger settlement for the PARENT (outbound) job
    if ((job as any).is_return_leg && (job as any).parent_job_id) {
      console.log('[SubcontractSettlement] Return leg completed. Redirecting to parent outbound job:', (job as any).parent_job_id);
      return triggerSubcontractSettlement((job as any).parent_job_id, showToast);
    }

    // RETURN VEHICLE GUARD: If outbound job has a return vehicle, defer settlement until return POD is complete
    if ((job as any).has_return_vehicle) {
      const { data: returnLeg } = await supabase
        .from('jobs')
        .select('id, status, delivery_completed_at')
        .eq('parent_job_id', jobId)
        .eq('is_return_leg', true)
        .maybeSingle();

      if (!returnLeg || !returnLeg.delivery_completed_at) {
        console.log('[SubcontractSettlement] DEFERRED: Outbound job has a return vehicle pending. Settlement will trigger after return leg POD completion.');
        showToast?.({
          title: "Subcontract Settlement Deferred",
          description: "Settlement invoice will be created once the return vehicle proof of delivery is completed.",
        });
        return { success: true, invoiceCreated: false, skippedReason: 'internal_job' as const };
      }
      console.log('[SubcontractSettlement] Return leg is completed, proceeding with settlement');
    }

    const ownerCompanyId = job.company_id;
    const executingCompanyId = (job as any).executing_company_id;
    const executionType = (job as any).execution_type;

    // Skip if internal job
    if (!executingCompanyId || executionType === 'internal' || executingCompanyId === ownerCompanyId) {
      console.log('[SubcontractSettlement] Skipped: internal job');
      return { success: true, invoiceCreated: false, skippedReason: 'internal_job' };
    }

    console.log('[SubcontractSettlement] Subcontracted job detected. Owner:', ownerCompanyId, 'Executor:', executingCompanyId);

    // Find active rate card (latest effective_from)
    const { data: rateCards, error: rcError } = await supabase
      .from('rate_cards' as any)
      .select('*')
      .eq('from_company_id', ownerCompanyId)
      .eq('to_company_id', executingCompanyId)
      .eq('active', true)
      .lte('effective_from', new Date().toISOString().split('T')[0])
      .order('effective_from', { ascending: false })
      .limit(1);

    if (rcError) {
      console.error('[SubcontractSettlement] Rate card fetch error:', rcError);
      return { success: false, invoiceCreated: false, error: rcError.message };
    }

    const rateCard = (rateCards as any[])?.[0];

    if (!rateCard) {
      console.warn('[SubcontractSettlement] No active rate card found. Creating settlement exception.');

      await supabase
        .from('settlement_exceptions' as any)
        .insert({
          job_id: jobId,
          owner_company_id: ownerCompanyId,
          executing_company_id: executingCompanyId,
          reason: 'no_rate_card',
          message: `No active rate card found between owner and subcontractor for job ${job.vehicle_registration}`,
        });

      showToast?.({
        title: "Settlement Exception",
        description: `No rate card set for this subcontractor. Please configure a rate card to enable auto-invoicing.`,
        variant: "destructive",
      });

      return { success: false, invoiceCreated: false, skippedReason: 'no_rate_card' };
    }

    // Build description from job details
    const description = [
      `Job Ref: ${job.invoice_number || job.id.slice(0, 8)}`,
      `Vehicle: ${job.vehicle_registration} - ${job.vehicle_make} ${job.vehicle_model}`,
      `Collection: ${job.collection_address || 'N/A'}`,
      `Delivery: ${job.delivery_address}`,
      job.delivery_completed_at
        ? `Delivered: ${new Date(job.delivery_completed_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}`
        : null,
    ].filter(Boolean).join('\n');

    // Idempotency check: return existing invoice reference if one already exists for this job+direction
    const { data: existing } = await supabase
      .from('subcontract_invoices' as any)
      .select('id, invoice_number, status, amount')
      .eq('job_id', jobId)
      .eq('from_company_id', executingCompanyId)
      .eq('to_company_id', ownerCompanyId)
      .neq('status', 'voided')
      .maybeSingle();

    if (existing) {
      console.log('[SubcontractSettlement] Invoice already exists for this job:', (existing as any).id, (existing as any).invoice_number);
      return { 
        success: true, 
        invoiceCreated: false, 
        skippedReason: 'already_exists',
        existingInvoiceId: (existing as any).id,
        existingInvoiceNumber: (existing as any).invoice_number,
      };
    }

    // Calculate invoice amount based on pricing type
    // SAFEGUARD: percent_discount pricing uses FULL customer job total (outbound + return legs) * (1 - discount/100)
    let invoiceAmount: number;
    const pricingType = rateCard.pricing_type;
    
    if (pricingType === 'percent_discount') {
      let customerTotal = Number(job.invoice_amount) || Number(job.estimated_cost) || 0;
      
      // If job has a return vehicle, include the return leg's amount in the total
      if ((job as any).has_return_vehicle) {
        const { data: returnLeg } = await supabase
          .from('jobs')
          .select('invoice_amount, estimated_cost')
          .eq('parent_job_id', jobId)
          .eq('is_return_leg', true)
          .maybeSingle();
        
        if (returnLeg) {
          const returnAmount = Number(returnLeg.invoice_amount) || Number(returnLeg.estimated_cost) || 0;
          customerTotal += returnAmount;
          console.log(`[SubcontractSettlement] Including return leg amount: ${formatCurrency(returnAmount.toFixed(2))} → total: ${formatCurrency(customerTotal.toFixed(2))}`);
        }
      }
      
      const discountPercent = Number(rateCard.percent_discount) || 0;
      invoiceAmount = customerTotal * (1 - discountPercent / 100);
      console.log(`[SubcontractSettlement] Percent discount pricing: customer total ${formatCurrency(customerTotal)} - ${discountPercent}% = ${formatCurrency(invoiceAmount.toFixed(2))}`);
    } else {
      // Fixed amount pricing
      invoiceAmount = Number(rateCard.fixed_amount) || 0;
      console.log(`[SubcontractSettlement] Fixed pricing: ${formatCurrency(invoiceAmount.toFixed(2))}`);
    }

    // Create subcontract invoice
    const { data: invoice, error: invError } = await supabase
      .from('subcontract_invoices' as any)
      .insert({
        job_id: jobId,
        from_company_id: executingCompanyId,
        to_company_id: ownerCompanyId,
        rate_card_id: rateCard.id,
        amount: invoiceAmount,
        currency: rateCard.currency,
        vat_applicable: rateCard.vat_applicable,
        status: 'draft',
        method: 'transportmate',
        description,
      })
      .select()
      .single();

    if (invError) {
      // Handle unique constraint violation (race condition: another call created it first)
      if (invError.code === '23505') {
        console.log('[SubcontractSettlement] Duplicate prevented by DB constraint, fetching existing');
        const { data: raceExisting } = await supabase
          .from('subcontract_invoices' as any)
          .select('id, invoice_number')
          .eq('job_id', jobId)
          .eq('from_company_id', executingCompanyId)
          .eq('to_company_id', ownerCompanyId)
          .neq('status', 'voided')
          .maybeSingle();
        return { 
          success: true, 
          invoiceCreated: false, 
          skippedReason: 'already_exists',
          existingInvoiceId: (raceExisting as any)?.id,
          existingInvoiceNumber: (raceExisting as any)?.invoice_number,
        };
      }
      console.error('[SubcontractSettlement] Invoice creation error:', invError);
      return { success: false, invoiceCreated: false, error: invError.message };
    }

    const invoiceId = (invoice as any).id;
    console.log('[SubcontractSettlement] Subcontract invoice created:', invoiceId);

    // Generate PDF (non-blocking — if it fails, invoice still exists as draft)
    try {
      console.log('[SubcontractSettlement] Generating PDF...');
      await supabase.functions.invoke('generate-subcontract-invoice-pdf', {
        body: { invoiceId },
      });
      console.log('[SubcontractSettlement] PDF generated successfully');
    } catch (pdfErr) {
      console.error('[SubcontractSettlement] PDF generation failed (non-fatal):', pdfErr);
    }

    // Sync to subcontractor's accounting software (non-blocking)
    let accountingSyncSucceeded = false;
    let accountingSyncProvider: string | null = null;
    try {
      console.log('[SubcontractSettlement] Syncing to accounting software...');
      const { data: syncResult } = await supabase.functions.invoke('sync-subcontract-invoice', {
        body: { invoiceId },
      });
      if (syncResult?.ok && syncResult?.provider) {
        accountingSyncSucceeded = true;
        accountingSyncProvider = syncResult.provider;
        console.log(`[SubcontractSettlement] Synced to ${syncResult.provider}: ${syncResult.externalInvoiceId}`);
      } else if (syncResult?.skipped) {
        console.log('[SubcontractSettlement] Accounting sync skipped:', syncResult.reason);
      } else if (syncResult?.error) {
        console.warn('[SubcontractSettlement] Accounting sync failed (non-fatal):', syncResult.error);
      }
    } catch (syncErr) {
      console.error('[SubcontractSettlement] Accounting sync error (non-fatal):', syncErr);
    }

    // Check billing automation settings
    const { data: relationships } = await supabase
      .from('subcontractor_relationships' as any)
      .select('*')
      .or(
        `and(owner_company_id.eq.${ownerCompanyId},subcontractor_company_id.eq.${executingCompanyId}),` +
        `and(owner_company_id.eq.${executingCompanyId},subcontractor_company_id.eq.${ownerCompanyId})`
      )
      .limit(1);

    const rel = (relationships as any[])?.[0];

    // If accounting software synced successfully, suppress TM email dispatch
    // — the accounting platform handles customer invoicing (same logic as autoInvoicing.ts)
    if (accountingSyncSucceeded) {
      console.log(`[SubcontractSettlement] EMAIL_SUPPRESSED: Accounting sync to ${accountingSyncProvider} succeeded — invoice delivery handled by accounting software`);
      
      // Mark as sent via accounting (not via TM email)
      await supabase
        .from('subcontract_invoices' as any)
        .update({ status: 'sent' })
        .eq('id', invoiceId);

      showToast?.({
        title: "Subcontract Invoice Synced",
        description: `Invoice for ${formatCurrency(invoiceAmount.toFixed(2))} synced to ${accountingSyncProvider}. Customer invoicing handled by accounting software.`,
      });
    } else {
      // No accounting sync succeeded — ALWAYS auto-send via TM email as fallback
      // This ensures subcontract invoices are delivered even if:
      // - The subcontractor has no accounting integration
      // - The accounting sync failed
      // - The subcontractor has auto-invoicing disabled for their own customers
      console.log('[SubcontractSettlement] Auto-sending subcontract invoice via TM email (no successful accounting sync)');

      try {
        await supabase.functions.invoke('send-subcontract-invoice', {
          body: { invoiceId },
        });

        await supabase
          .from('subcontract_invoices' as any)
          .update({ status: 'sent', email_sent_at: new Date().toISOString() })
          .eq('id', invoiceId);

        showToast?.({
          title: "Subcontract Invoice Sent",
          description: `Invoice for ${formatCurrency(invoiceAmount.toFixed(2))} sent to owner company.`,
        });
      } catch (sendErr: any) {
        console.error('[SubcontractSettlement] Failed to send invoice:', sendErr);
        showToast?.({
          title: "Subcontract Invoice Created",
          description: `Invoice created but email sending failed. You can resend manually.`,
          variant: "destructive",
        });
      }
    }

    return { success: true, invoiceCreated: true, invoiceId };
  } catch (error: any) {
    console.error('[SubcontractSettlement] Exception:', error);
    return { success: false, invoiceCreated: false, error: error.message };
  }
}
